<template>
    <div class="cred-page">
        <section class="cred-panel" :aria-labelledby="titleId">
            <ChannelConnectionDiagramCmp :channel="channel" :state="diagramState" :failed="diagramFailed" :idle-icon="idleIcon" />

            <h2 class="cred-title" :id="titleId">{{title}}</h2>
            <p class="cred-lead" v-if="lead">{{lead}}</p>

            <a v-if="tutorialLink"
                class="cred-guide"
                :href="tutorialLink"
                target="_blank"
                rel="noopener noreferrer">
                <span class="cred-guide-icon" aria-hidden="true">
                    <fa icon="question-circle" />
                </span>
                <span class="cred-guide-text">
                    <strong>{{tutorialTitle}}</strong>
                    <span>{{tutorialDescription}}</span>
                </span>
                <fa icon="external-link-alt" class="cred-guide-arrow" aria-hidden="true" />
                <span class="sr-only">(abre em nova aba)</span>
            </a>

            <div class="cred-body">
                <slot />
            </div>
        </section>

        <div class="cred-footer" v-if="$slots['footer-start'] || $slots['footer-end']">
            <div class="cred-footer-start">
                <slot name="footer-start" />
            </div>
            <div class="cred-footer-end">
                <slot name="footer-end" />
            </div>
        </div>
    </div>
</template>

<script>
import ChannelConnectionDiagramCmp from '@/channel/components/ChannelConnectionDiagramCmp.vue'

/**
 * Moldura das variações da tela de credenciais: diagrama, título, atalho para o
 * passo a passo do canal, corpo (slot) e rodapé fixo de ações (slots
 * `footer-start` e `footer-end`).
 */
export default {
    name: 'ChannelCredentialsPanelCmp',
    components: {
        ChannelConnectionDiagramCmp,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        diagramState: {
            type: String,
            default: 'idle',
        },
        diagramFailed: {
            type: Boolean,
            default: false,
        },
        idleIcon: {
            type: String,
            default: 'key',
        },
        title: {
            type: String,
            required: true,
        },
        lead: {
            type: String,
            default: '',
        },
        tutorialLink: {
            type: String,
            default: '',
        },
        tutorialTitle: {
            type: String,
            default: 'Onde encontro essas informações?',
        },
        tutorialDescription: {
            type: String,
            default: 'Veja o passo a passo para gerar as credenciais no canal.',
        },
    },
    computed: {
        titleId() {
            return `cred-title-${this._uid}`
        },
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;

.cred-page {
    animation: cred-rise .5s $ease-out both;
}

.cred-panel {
    margin-top: 16px;
    padding: 32px 36px;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
}

.cred-title {
    margin: 28px 0 0;
    color: var(--text-strong);
    font-family: $display;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -.4px;
    text-align: center;
}

.cred-lead {
    max-width: 540px;
    margin: 6px auto 0;
    color: var(--text-muted);
    font-size: 15px;
    line-height: 1.5;
    text-align: center;
}

/* ── Atalho para o passo a passo ── */

.cred-guide {
    display: flex;
    align-items: center;
    gap: 14px;
    max-width: 560px;
    margin: 22px auto 0;
    padding: 12px 16px;
    border: 1px solid var(--tint-brand-border);
    border-radius: 12px;
    background: var(--tint-brand-soft);
    color: inherit;
    text-decoration: none;
    transition: background-color .15s, border-color .15s, transform .15s $ease-out;

    &:hover,
    &:focus-visible {
        border-color: var(--brand-soft);
        background: var(--tint-brand-hover);
        color: inherit;
        text-decoration: none;
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: 2px solid var(--brand-soft);
        outline-offset: 2px;
    }
}

.cred-guide-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: var(--surface-1);
    color: var(--brand);
    font-size: 16px;
}

.cred-guide-text {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;

    strong {
        color: var(--text-strong);
        font-size: 14px;
    }

    span {
        color: var(--text-muted);
        font-size: 13px;
    }
}

.cred-guide-arrow {
    flex-shrink: 0;
    color: var(--brand-soft);
    font-size: 13px;
}

.cred-body {
    max-width: 560px;
    margin: 28px auto 0;
}

/* ── Rodapé fixo ── */

.cred-footer {
    position: sticky;
    bottom: 12px;
    z-index: 5;
    margin-top: 16px;
    padding: 10px;
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    background: color-mix(in srgb, var(--surface-1) 92%, transparent);
    box-shadow: var(--shadow-sm), 0 12px 28px -18px color-mix(in srgb, var(--shadow-ink) 45%, transparent);
    backdrop-filter: blur(8px);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    // Botões que chegam pelos slots: ícone + texto alinhados, mesmo padrão da autenticação
    ::v-deep .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    // O override global do btn-primary não cobre o desabilitado: sem isto vira azul
    ::v-deep .btn-primary:disabled {
        border-color: var(--brand-solid);
        background-color: var(--brand-solid);
        opacity: .65;
    }
}

.cred-footer-start,
.cred-footer-end {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
}

.cred-footer-end {
    margin-left: auto;
}

@keyframes cred-rise {
    from { opacity: 0; translate: 0 10px; }
    to   { opacity: 1; translate: 0 0; }
}

@media (max-width: 576px) {
    .cred-panel {
        padding: 24px 16px;
    }

    .cred-title {
        font-size: 20px;
    }

    // No celular os botões empilham: fixo, o rodapé cobriria metade da tela
    .cred-footer {
        position: static;
    }

    .cred-footer-start,
    .cred-footer-end {
        width: 100%;
    }

    .cred-footer-end {
        flex-direction: column-reverse;

        ::v-deep .btn {
            width: 100%;
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .cred-page,
    .cred-guide {
        animation: none !important;
        transition: none;
    }
}
</style>
